import React, { useEffect, useRef, useState, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Realistic lightning + thunder atmosphere.
 * Visual intensity reduced 24%.
 *
 * Realism upgrades:
 * - Bolt path is jagged with multiple branches and varying segment lengths
 * - Flash uses an authentic flicker train (5 short returns over ~700ms)
 *   modeled on real lightning return strokes
 * - Thunder is a 3-layer synth: initial crack (filtered noise impulse),
 *   main rumble (band-pass swept brown noise), distant rolling tail
 *   with random low-frequency LFO — like rumble bouncing off clouds.
 */
export const Lightning = () => {
  const [flash, setFlash] = useState(0); // current flicker frame intensity 0..1
  const [bolt, setBolt] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const audioCtxRef = useRef(null);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const after = (ms, fn) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
  };

  // ------- Generate a jagged bolt path procedurally -------
  const buildBolt = () => {
    const startX = 30 + Math.random() * 8;
    const segments = 14 + Math.floor(Math.random() * 6);
    let x = startX;
    const points = [`M ${x} 0`];
    const segLen = 1000 / segments;
    for (let i = 1; i <= segments; i++) {
      x += (Math.random() - 0.5) * 36;
      const y = i * segLen + (Math.random() - 0.5) * 8;
      points.push(`L ${x} ${y}`);
    }
    const mainPath = points.join(" ");

    // 2-3 random branches forking off the main spine
    const branches = [];
    const branchCount = 2 + Math.floor(Math.random() * 2);
    for (let b = 0; b < branchCount; b++) {
      const startSeg = 2 + Math.floor(Math.random() * (segments - 5));
      const bSegs = 4 + Math.floor(Math.random() * 4);
      // approximate start point along main path
      const px = startX + (Math.random() - 0.5) * 20;
      const py = startSeg * segLen;
      const sign = Math.random() > 0.5 ? 1 : -1;
      const parts = [`M ${px} ${py}`];
      let bx = px;
      let by = py;
      for (let i = 1; i <= bSegs; i++) {
        bx += sign * (8 + Math.random() * 20);
        by += segLen * 0.6;
        parts.push(`L ${bx} ${by}`);
      }
      branches.push(parts.join(" "));
    }

    return {
      key: Date.now() + Math.random(),
      x: 6 + Math.random() * 88, // horizontal placement %
      scaleX: Math.random() > 0.5 ? 1 : -1,
      mainPath,
      branches,
    };
  };

  // ------- Realistic thunder via Web Audio API -------
  const playThunder = useCallback(() => {
    if (!soundOn) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();

      const now = ctx.currentTime;
      const sampleRate = ctx.sampleRate;

      // Helper to build a noise buffer with brownian bias for low rumble
      const buildNoise = (durationSec, brownAmount = 0.025) => {
        const size = Math.floor(sampleRate * durationSec);
        const buf = ctx.createBuffer(1, size, sampleRate);
        const data = buf.getChannelData(0);
        let last = 0;
        for (let i = 0; i < size; i++) {
          const white = Math.random() * 2 - 1;
          last = (last + brownAmount * white) / (1 + brownAmount);
          data[i] = last * 3.5;
        }
        return buf;
      };

      // ----- Layer 1: initial crack (sharp, ~120ms) -----
      const crackBuf = buildNoise(0.4, 0.05);
      const crack = ctx.createBufferSource();
      crack.buffer = crackBuf;
      const crackBP = ctx.createBiquadFilter();
      crackBP.type = "bandpass";
      crackBP.frequency.value = 1600;
      crackBP.Q.value = 0.6;
      const crackGain = ctx.createGain();
      crackGain.gain.setValueAtTime(0.0001, now);
      crackGain.gain.exponentialRampToValueAtTime(0.42, now + 0.015);
      crackGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      crack.connect(crackBP).connect(crackGain).connect(ctx.destination);
      crack.start(now);
      crack.stop(now + 0.45);

      // ----- Layer 2: main rumble (3.5s low sweep) -----
      const rumbleBuf = buildNoise(4.2, 0.02);
      const rumble = ctx.createBufferSource();
      rumble.buffer = rumbleBuf;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(700, now + 0.05);
      lp.frequency.exponentialRampToValueAtTime(90, now + 4.0);
      lp.Q.value = 0.9;
      const rumbleGain = ctx.createGain();
      rumbleGain.gain.setValueAtTime(0.0001, now);
      rumbleGain.gain.exponentialRampToValueAtTime(0.65, now + 0.18);
      rumbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 4.0);
      // Slight tremolo to simulate rumble pulsation
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 3 + Math.random() * 2;
      lfoGain.gain.value = 0.15;
      lfo.connect(lfoGain).connect(rumbleGain.gain);
      rumble.connect(lp).connect(rumbleGain).connect(ctx.destination);
      rumble.start(now + 0.05);
      rumble.stop(now + 4.3);
      lfo.start(now);
      lfo.stop(now + 4.3);

      // ----- Layer 3: distant rolling tail (delayed, very low) -----
      const tailDelay = 1.4 + Math.random() * 0.8;
      const tailBuf = buildNoise(3.0, 0.018);
      const tail = ctx.createBufferSource();
      tail.buffer = tailBuf;
      const tailLP = ctx.createBiquadFilter();
      tailLP.type = "lowpass";
      tailLP.frequency.setValueAtTime(180, now + tailDelay);
      tailLP.frequency.exponentialRampToValueAtTime(55, now + tailDelay + 2.5);
      const tailGain = ctx.createGain();
      tailGain.gain.setValueAtTime(0.0001, now + tailDelay);
      tailGain.gain.exponentialRampToValueAtTime(0.32, now + tailDelay + 0.4);
      tailGain.gain.exponentialRampToValueAtTime(0.0001, now + tailDelay + 2.8);
      tail.connect(tailLP).connect(tailGain).connect(ctx.destination);
      tail.start(now + tailDelay);
      tail.stop(now + tailDelay + 3.1);
    } catch (e) {
      // decorative — swallow
    }
  }, [soundOn]);

  // ------- Strike sequence -------
  const strike = useCallback(() => {
    clearTimers();
    const b = buildBolt();
    setBolt(b);

    // Realistic flicker train. Values are intensity 0..1 BEFORE 24% reduction
    // (reduction applied at render time).
    // Pattern based on natural return-stroke timing.
    const train = [
      { at: 0,    v: 1.0,  hold: 60 },   // primary
      { at: 90,   v: 0.0,  hold: 50 },
      { at: 150,  v: 0.55, hold: 70 },   // 2nd return
      { at: 230,  v: 0.0,  hold: 80 },
      { at: 320,  v: 0.4,  hold: 60 },   // 3rd
      { at: 400,  v: 0.0,  hold: 100 },
      { at: 520,  v: 0.22, hold: 50 },   // dim 4th
      { at: 580,  v: 0.0,  hold: 0 },
    ];

    train.forEach((step) => {
      after(step.at, () => setFlash(step.v));
    });
    // Bolt visual stays through the first ~450ms then fades
    after(480, () => setBolt(null));

    // Thunder rolls in 400-1100ms after the strike begins (closer = faster)
    after(420 + Math.random() * 700, playThunder);
  }, [playThunder]);

  useEffect(() => {
    let timer;
    const schedule = () => {
      // Irregular gaps — real storms have variable rhythm
      const delay = 6500 + Math.random() * 11000; // 6.5–17.5s
      timer = setTimeout(() => {
        strike();
        schedule();
      }, delay);
    };
    timer = setTimeout(() => {
      strike();
      schedule();
    }, 2400 + Math.random() * 2000);
    return () => {
      clearTimeout(timer);
      clearTimers();
    };
  }, [strike]);

  // 24% reduction applied here
  const VISIBILITY = 0.76;
  const flashOpacity = flash * VISIBILITY;
  const boltOpacity = bolt ? VISIBILITY : 0;

  return (
    <>
      {/* Full-screen flash */}
      <div
        data-testid="lightning-flash"
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-[55] mix-blend-screen"
        style={{
          backgroundColor: "#F6F0DC",
          opacity: flashOpacity,
          transition: flash === 0 ? "opacity 220ms ease-out" : "opacity 18ms linear",
        }}
      />

      {/* SVG bolt */}
      {bolt && (
        <svg
          key={bolt.key}
          data-testid="lightning-bolt"
          aria-hidden="true"
          className="fixed inset-0 w-full h-full pointer-events-none z-[56]"
          viewBox="0 0 1000 1000"
          preserveAspectRatio="xMidYMid slice"
          style={{ opacity: boltOpacity }}
        >
          <defs>
            <linearGradient id="bolt-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFFCEB" stopOpacity="1" />
              <stop offset="55%" stopColor="#E6A817" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#8C1E1E" stopOpacity="0.35" />
            </linearGradient>
            <filter id="bolt-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g
            transform={`translate(${bolt.x * 10} 0) scale(${bolt.scaleX} 1) ${
              bolt.scaleX === -1 ? "translate(-60 0)" : ""
            }`}
            filter="url(#bolt-glow)"
          >
            {/* Outer glow stroke */}
            <path
              d={bolt.mainPath}
              stroke="url(#bolt-grad)"
              strokeWidth="3.5"
              fill="none"
              strokeLinejoin="miter"
              strokeLinecap="round"
              opacity="0.85"
            />
            {/* Hot white inner core */}
            <path
              d={bolt.mainPath}
              stroke="#FFFFFF"
              strokeWidth="1.2"
              fill="none"
              opacity="0.95"
              strokeLinejoin="miter"
              strokeLinecap="round"
            />
            {/* Branches */}
            {bolt.branches.map((p, i) => (
              <path
                key={i}
                d={p}
                stroke="url(#bolt-grad)"
                strokeWidth="1.6"
                fill="none"
                opacity="0.65"
                strokeLinejoin="miter"
                strokeLinecap="round"
              />
            ))}
          </g>
        </svg>
      )}

      {/* Sound toggle */}
      <button
        data-testid="lightning-sound-toggle"
        onClick={() => {
          try {
            if (!audioCtxRef.current) {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              if (Ctx) audioCtxRef.current = new Ctx();
            }
            if (audioCtxRef.current?.state === "suspended") {
              audioCtxRef.current.resume();
            }
          } catch (_) {}
          setSoundOn((v) => !v);
        }}
        aria-label={soundOn ? "Mute thunder" : "Unmute thunder"}
        className={`fixed bottom-6 left-6 z-[80] w-11 h-11 rounded-full flex items-center justify-center border backdrop-blur-md transition-all ${
          soundOn
            ? "bg-gold/20 border-gold text-gold shadow-[0_0_24px_rgba(212,175,55,0.5)]"
            : "bg-ink-950/70 border-white/15 text-slate-400 hover:text-gold hover:border-gold/50"
        }`}
        title={soundOn ? "Thunder: on" : "Thunder: off (click to enable)"}
      >
        {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>
    </>
  );
};
